import React, { memo, useCallback } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { ItemsAutocomplete } from 'ui/components/Autocomplete/ItemsAutocomplete';
import { LocationsAsyncAutocomplete } from 'ui/components/Autocomplete/LocationsAsyncAutocomplete';
import { VendorAutocomplete } from 'ui/components/Autocomplete/VendorAutocomplete';
import { Location } from 'services/locations';
import { Item } from 'services/items';
import { useHandleCheckboxChange } from 'services/forms/hooks';
import { Vendor } from 'services/vendors';

import { ReportFiltersProps } from '../../types';

const VendorFulfillmentHistoryFilters: React.FC<ReportFiltersProps> = (
  props
) => {
  const { parameters, setParameters } = props;

  const handleLocationChange = useCallback(
    (location: Location | null) => {
      setParameters((old) => ({
        ...old,
        locationId: location ? location.id : null,
      }));
    },
    [setParameters]
  );

  const handleVendorChange = (vendor: Vendor | null) => {
    setParameters((old) => ({ ...old, vendorId: vendor ? vendor.id : null }));
  };

  const handleItemChange = useCallback(
    (selectedItem: Item | null) => {
      setParameters((old) => ({
        ...old,
        itemId: selectedItem ? selectedItem.id : null,
      }));
    },
    [setParameters]
  );

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setParameters((old) => ({
        ...old,
        showFulfilled: checked,
        showPartial: checked,
        selectAllOptional: checked,
      }));
    },
    [setParameters]
  );

  const handleCheckboxChange = useHandleCheckboxChange(setParameters);

  return (
    <>
      <Box px={2} pb={0} pt={1}>
        <FormControlLabel
          control={
            <Checkbox
              className="redesign"
              checked={Boolean(parameters.selectAllOptional)}
              onChange={handleSelectAll}
              color="primary"
            />
          }
          label="Select All Optional"
        />
      </Box>
      <Accordion square defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography>
            <b>Optional</b>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" width="100%" pt={1}>
            <Box mb={1}>
              <LocationsAsyncAutocomplete
                label="Location"
                value={parameters.locationId}
                placeholder="Select location"
                companyWide={false}
                onChange={handleLocationChange}
                customQuickSearchColumns={['name', 'description']}
              />
            </Box>
            <Box mb={1}>
              <VendorAutocomplete
                label="Vendor"
                onChange={handleVendorChange}
                value={parameters.vendorId as number | null}
                placeholder="Select Vendor"
              />
            </Box>
            <Box mb={1}>
              <ItemsAutocomplete
                label="Item"
                placeholder="Select item"
                onChange={handleItemChange}
                value={parameters.itemId}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  className="redesign"
                  checked={Boolean(parameters.showFulfilled)}
                  onChange={handleCheckboxChange}
                  color="primary"
                  name="showFulfilled"
                />
              }
              label="Fulfilled"
            />
            <FormControlLabel
              control={
                <Checkbox
                  className="redesign"
                  checked={Boolean(parameters.showPartial)}
                  onChange={handleCheckboxChange}
                  color="primary"
                  name="showPartial"
                />
              }
              label="Partial"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default memo(VendorFulfillmentHistoryFilters);
